import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "fw_theme";
const CUSTOM_STORAGE_KEY = "fw_custom_theme";
const THEMES = ["light", "dark", "brown", "pastel", "custom"];

const DEFAULT_CUSTOM_THEME = {
  pageBg: "#0f172a",
  pageFg: "#f9fafb",
  cardBg: "#111827",
  cardFg: "#e5e7eb",
  mutedBg: "#1f2937",
  mutedBg2: "#374151",
  border: "#4b5563",
  mutedText: "#9ca3af",
  accentBg: "#f97316",
  accentFg: "#111827"
};

const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
  customTheme: DEFAULT_CUSTOM_THEME,
  setCustomTheme: () => {}
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "light";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && THEMES.includes(stored)) return stored;
    return "light";
  });

  const [customTheme, setCustomThemeState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_CUSTOM_THEME;
    try {
      const raw = window.localStorage.getItem(CUSTOM_STORAGE_KEY);
      if (!raw) return DEFAULT_CUSTOM_THEME;
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_CUSTOM_THEME, ...parsed };
    } catch {
      return DEFAULT_CUSTOM_THEME;
    }
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;

    const cssTheme = theme === "custom" ? "dark" : theme;

    THEMES.forEach((t) => root.classList.remove(`theme-${t}`));
    root.classList.add(`theme-${cssTheme}`);
    root.setAttribute("data-theme", cssTheme);

    if (theme === "custom") {
      const t = customTheme || DEFAULT_CUSTOM_THEME;
      root.style.setProperty("--page-bg", t.pageBg);
      root.style.setProperty("--page-fg", t.pageFg);
      root.style.setProperty("--card-bg", t.cardBg);
      root.style.setProperty("--card-fg", t.cardFg);
      root.style.setProperty("--muted-bg", t.mutedBg);
      root.style.setProperty("--muted-bg2", t.mutedBg2);
      root.style.setProperty("--border", t.border);
      root.style.setProperty("--muted-text", t.mutedText);
      root.style.setProperty("--accent-bg", t.accentBg);
      root.style.setProperty("--accent-fg", t.accentFg);
    } else {
      root.style.removeProperty("--page-bg");
      root.style.removeProperty("--page-fg");
      root.style.removeProperty("--card-bg");
      root.style.removeProperty("--card-fg");
      root.style.removeProperty("--muted-bg");
      root.style.removeProperty("--muted-bg2");
      root.style.removeProperty("--border");
      root.style.removeProperty("--muted-text");
      root.style.removeProperty("--accent-bg");
      root.style.removeProperty("--accent-fg");
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme, customTheme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        CUSTOM_STORAGE_KEY,
        JSON.stringify(customTheme)
      );
    } catch {}
  }, [customTheme]);

  const value = useMemo(
    () => ({
      theme,
      customTheme,
      setTheme: (next) => {
        if (!THEMES.includes(next)) return;
        setThemeState(next);
      },
      setCustomTheme: (updater) => {
        setCustomThemeState((prev) => {
          const next =
            typeof updater === "function" ? updater(prev) : updater;
          return { ...prev, ...next };
        });
        setThemeState("custom");
      }
    }),
    [theme, customTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
